import React, { useState } from 'react';
import { Search, User, DollarSign, Shield, Cpu, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import heroImg from "../assets/faq-img.jpg";

const faqs = [
  { question: 'How do I invest in Nadezhda Venture?', answer: 'You can invest through our platform by creating an account, completing KYC, and selecting an active investment round.' },
  { question: 'What is the minimum investment amount?', answer: 'The minimum investment amount varies by project but generally starts at $1,000.' },
  { question: 'Is my investment secured?', answer: 'All investments are subject to risk. However, we perform due diligence to minimize potential risks.' },
  { question: 'Can I withdraw my investment anytime?', answer: 'Withdrawal options depend on the specific investment terms. Some allow early exit; others may lock funds for a period.' },
  { question: 'Do you charge any platform fees?', answer: 'Yes, we charge a nominal service fee which will be disclosed before investment confirmation.' }
];

const categories = [
  { title: 'Account & Settings', description: 'Manage your profile, KYC and security settings.', icon: <User className="h-6 w-6 text-indigo-600" /> },
  { title: 'Investment Plans', description: 'Explore current and upcoming investment opportunities.', icon: <DollarSign className="h-6 w-6 text-green-600" /> },
  { title: 'Payments & Billing', description: 'Learn about deposits, withdrawals, and fee structures.', icon: <DollarSign className="h-6 w-6 text-yellow-600" /> },
  { title: 'Legal & Risk', description: 'Understand the terms, policies, and risk disclosures.', icon: <Shield className="h-6 w-6 text-red-600" /> },
  { title: 'Technical Support', description: 'Facing an issue? Here\'s how to solve technical problems.', icon: <Cpu className="h-6 w-6 text-blue-600" /> },
  { title: 'Partnerships', description: 'Join us as a partner, advisor, or contributor.', icon: <Users className="h-6 w-6 text-purple-600" /> },
];

const FAQPage = () => {
  const [isSearchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expandedIndex, setExpandedIndex] = useState<number[]>([]);

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(query.toLowerCase()) ||
    faq.answer.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="text-gray-900">
      {/* 🟨 Hero Section */}
     <section className="relative h-48 sm:h-60 md:h-80 lg:h-96 w-full overflow-hidden flex items-center justify-center text-white text-center">
  <img
    src={heroImg}
    alt="FAQ Hero"
    className="absolute inset-0 w-full h-full object-cover"
  />
  {/* REMOVED pt-72 FROM HERE */}
  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center pt-32">
    <div>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-orange-500 mb-2">We're Here to Help</h1>
      <p className="text-base sm:text-lg text-gray-200">Find answers to your most common questions</p>
    </div>
  </div>
</section>

      {/* 🟦 Card Section */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-6">
          <h2 className="text-2xl font-bold">Browse Help Topics</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              whileHover={{ scale: 1.03, y: -5 }}
              className="border border-gray-200 p-4 rounded-xl shadow hover:shadow-md transition cursor-pointer bg-white hover:bg-gray-50"
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-2">
                {cat.icon}
                <h3 className="font-semibold text-lg">{cat.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{cat.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 🟧 FAQ Section */}
      <div className="bg-gray-50 py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-center w-full">Frequently Asked Questions</h1>
          </div>

          {/* Search Bar */}
          <div className="flex justify-center mb-6">
            <div
              className="relative flex items-center bg-white p-1 rounded-full shadow-lg transition-all duration-300 ease-in-out"
              style={{ width: isSearchOpen ? '100%' : '56px', maxWidth: isSearchOpen ? '600px' : '56px' }}
              onMouseEnter={() => setSearchOpen(true)}
              onMouseLeave={() => setSearchOpen(false)}
            >
              <AnimatePresence initial={false}>
                {isSearchOpen && (
                  <motion.input
                    key="search-input"
                    type="text"
                    placeholder="Search here ..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-grow p-2 pl-6 pr-4 rounded-full outline-none text-lg bg-transparent"
                    initial={{ width: 0, opacity: 0, paddingLeft: 0, paddingRight: 0 }}
                    animate={{ width: '100%', opacity: 1, paddingLeft: '1.5rem', paddingRight: '1rem' }}
                    exit={{ width: 0, opacity: 0, paddingLeft: 0, paddingRight: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </AnimatePresence>
              <button className="bg-orange-600 text-white p-3 rounded-full hover:bg-orange-700 focus:outline-none transition-all duration-900">
                <Search className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* 🟧 Accordion-style FAQ List (multi-open, smooth + larger content) */}
          <div className="space-y-4">
            {filteredFaqs.map((faq, idx) => {
              const isOpen = expandedIndex?.includes(idx);

              return (
                <div
                  key={idx}
                  className={`border rounded-lg transition-all duration-300 ease-in-out ${
                    isOpen ? "bg-white border-orange-500" : "bg-white border-gray-200"
                  }`}
                >
                  <button
                    onClick={() => {
                      setExpandedIndex(prev => {
                        if (!prev) return [idx];
                        return prev.includes(idx)
                          ? prev.filter(i => i !== idx)
                          : [...prev, idx];
                      });
                    }}
                    className="w-full p-5 flex justify-between items-center text-left text-[1.1rem] font-semibold text-gray-800 focus:outline-none"
                  >
                    {faq.question}
                    <span
                      className={`flex items-center justify-center h-9 w-9 rounded-full border-2 transition-all ${
                        isOpen ? "border-orange-500 text-orange-500" : "border-orange-400 text-orange-400"
                      }`}
                    >
                      {isOpen ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="faq-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden px-6 pb-6 text-gray-700 text-[1rem] leading-relaxed"
                      >
                        {faq.answer}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}

            {filteredFaqs.length === 0 && (
              <p className="text-gray-500 text-center py-4">No results found for "{query}"</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;